import React from "react";

import type {
  Procedure,
  ProcedureDetail,
  ProcValidation,
  SmartApplyValidationResult,
} from "../../../../application/domain/proc-management";
import {
  ProcedureAcceptAppOnlineInput,
  ProcedureApplicantsListInput,
  ProcedureOnlineInfoApplicationUrlInput,
  ProcedureOnlineInfoSmartApplyIdInput,
  ProcedureOnlineInfoUseSmartApplyInput,
  ProcedureOnlineInfoNoticeInput,
  ProcedureOnlineInfoProxyApplyConditionInput,
  ProcedureOnlineInfoFeeInput,
} from "../../../molecules/proc-management/form";
import { ServiceProviderR } from "../../../pages/service-provider/routes";
import { SmartApplyR } from "../../../pages/smart-apply/route";

type Props = {
  procedure: ProcedureDetail | Procedure;
  onChange: (path: (string | number)[], value: any) => void;
  isServiceProviderAccount?: boolean;
  minimizeForInputInSteps?: boolean;
  disabled?: boolean;
  shouldDisplayError?: boolean;
  validationErrors: ProcValidation;
  smartApplyValidationResult: SmartApplyValidationResult;
};

export const OnlineInfoSection: React.FC<Props> = ({
  procedure,
  onChange,
  isServiceProviderAccount = false,
  minimizeForInputInSteps = false,
  disabled = false,
  shouldDisplayError = false,
  validationErrors,
  smartApplyValidationResult,
}) => {
  const { acceptAppOnline, onlineInfo, jurisdiction } = procedure;
  const applicants = onlineInfo?.applicants ?? [];

  const [useGrafferSmartApply, setUseGrafferSmartApply] =
    React.useState<boolean>(!!onlineInfo?.grafferSmartApplyInfo);
  React.useEffect(() => {
    if (useGrafferSmartApply) {
      onChange(
        ["onlineInfo", "grafferSmartApplyInfo"],
        onlineInfo?.grafferSmartApplyInfo ?? {},
      );
      onChange(["onlineInfo", "applicationUrl"], undefined);
    }
    if (!acceptAppOnline || !useGrafferSmartApply) {
      onChange(["onlineInfo", "grafferSmartApplyInfo"], undefined);
    }
  }, [useGrafferSmartApply, onlineInfo, acceptAppOnline, onChange]);

  const handleChangeAcceptAppOnline: Props["onChange"] = React.useCallback(
    (path, value): void => {
      if (value) {
        setUseGrafferSmartApply(value);
      }
      onChange(path, value);
    },
    [onChange, setUseGrafferSmartApply],
  );

  const linkForSmartApplyProcs = isServiceProviderAccount
    ? ServiceProviderR.Procedures.link({
        id: jurisdiction.organizationId,
      })
    : SmartApplyR.Procedures.link({});

  return (
    <section className="mb-5">
      <h2 className="subtitle">電子申請についての説明</h2>

      {!minimizeForInputInSteps && (
        <ProcedureAcceptAppOnlineInput
          value={acceptAppOnline}
          disabled={disabled}
          basePath={["acceptAppOnline"]}
          onChange={handleChangeAcceptAppOnline}
        />
      )}

      {acceptAppOnline && (
        <>
          <ProcedureApplicantsListInput
            value={applicants}
            onChange={onChange}
            disabled={disabled}
            basePath={["onlineInfo", "applicants"]}
            shouldDisplayError={shouldDisplayError}
            validationErrors={validationErrors.onlineInfo?.applicants ?? []}
          />

          <ProcedureOnlineInfoUseSmartApplyInput
            value={useGrafferSmartApply}
            onChange={setUseGrafferSmartApply}
            disabled={disabled}
          />

          {useGrafferSmartApply ? (
            <ProcedureOnlineInfoSmartApplyIdInput
              value={onlineInfo?.grafferSmartApplyInfo?.procedureId}
              onChange={onChange}
              basePath={["onlineInfo", "grafferSmartApplyInfo", "procedureId"]}
              disabled={disabled}
              linkForSmartApplyProcs={linkForSmartApplyProcs}
              isServiceProviderAccount={isServiceProviderAccount}
              orgId={jurisdiction.organizationId}
              shouldDisplayError={shouldDisplayError}
              errorMsg={
                validationErrors.onlineInfo?.grafferSmartApplyInfo ?? ""
              }
              currentStatus={procedure.contentStatus}
              smartApplyValidationResult={smartApplyValidationResult}
            />
          ) : (
            <ProcedureOnlineInfoApplicationUrlInput
              value={onlineInfo?.applicationUrl}
              onChange={onChange}
              basePath={["onlineInfo", "applicationUrl"]}
              disabled={disabled}
              shouldDisplayError={shouldDisplayError}
              errorMsg={validationErrors.onlineInfo?.applicationUrl ?? ""}
            />
          )}

          {!minimizeForInputInSteps && (
            <>
              <ProcedureOnlineInfoNoticeInput
                value={onlineInfo?.notice}
                onChange={onChange}
                basePath={["onlineInfo", "notice"]}
                disabled={disabled}
                shouldDisplayError={shouldDisplayError}
                errorMsg={validationErrors.onlineInfo?.notice ?? ""}
              />

              <ProcedureOnlineInfoProxyApplyConditionInput
                value={onlineInfo?.acceptAppByProxyIf}
                onChange={onChange}
                basePath={["onlineInfo", "acceptAppByProxyIf"]}
                disabled={disabled}
                shouldDisplayError={shouldDisplayError}
                errorMsg={validationErrors.onlineInfo?.acceptAppByProxyIf ?? ""}
              />
            </>
          )}

          <ProcedureOnlineInfoFeeInput
            value={onlineInfo?.fee}
            onChange={onChange}
            basePath={["onlineInfo", "fee"]}
            disabled={disabled}
            shouldDisplayError={shouldDisplayError}
            errorMsg={validationErrors.onlineInfo?.fee ?? ""}
          />
        </>
      )}
    </section>
  );
};
